export const BOARD_LANES = [
  {
    key: "novos",
    label: "Novos",
    description: "Aguardando aceite",
    statuses: ["novo", "recebido", "aguardando_aceite", "aguardando_pagamento"],
  },
  {
    key: "producao",
    label: "Produção",
    description: "Na cozinha ou no bar",
    statuses: ["aceito", "em_preparo"],
  },
  {
    key: "retirada",
    label: "Retirada",
    description: "Prontos no balcão",
    statuses: ["pronto", "entregue"],
    deliveryType: "pickup",
  },
  {
    key: "entrega",
    label: "Entrega",
    description: "Entregador, rota e recebimento",
    statuses: ["pronto", "saiu_para_entrega", "entregue"],
    deliveryType: "delivery",
  },
  {
    key: "finalizados",
    label: "Finalizados",
    description: "Últimas 24 horas",
    statuses: ["concluido", "cancelado", "recusado", "pagamento_expirado"],
    terminal: true,
  },
];

export const TERMINAL_STATUSES = [
  "concluido",
  "cancelado",
  "recusado",
  "pagamento_expirado",
];

export const STATUS_LABELS = {
  novo: "Novo",
  recebido: "Recebido",
  aguardando_pagamento: "Aguardando pagamento",
  pagamento_expirado: "Pagamento expirado",
  aguardando_aceite: "Aguardando aceite",
  aceito: "Aceito",
  em_preparo: "Em preparo",
  pronto: "Pronto",
  saiu_para_entrega: "Em rota",
  entregue: "Entregue",
  concluido: "Concluído",
  cancelado: "Cancelado",
  recusado: "Recusado",
};

export const PAYMENT_STATUS_LABELS = {
  pendente: "Pendente",
  aguardando: "Pendente",
  pago: "Pago",
  aprovado: "Pago",
  expirado: "Expirado",
  cancelado: "Cancelado",
  falhou: "Falhou",
};

export function emptyBoard() {
  return BOARD_LANES.reduce((result, lane) => {
    result[lane.key] = { items: [], total: 0, next_cursor: null };
    return result;
  }, {});
}

export function unwrapResponse(payload) {
  let value = payload;
  let depth = 0;
  while (
    value &&
    !Array.isArray(value) &&
    typeof value === "object" &&
    value.data &&
    typeof value.data === "object" &&
    depth < 3
  ) {
    value = value.data;
    depth += 1;
  }
  return value || {};
}

export function orderId(order) {
  return (
    order &&
    (order.id || order.pedido_online_id || order.codigo_publico || order.codigo)
  );
}

export function orderCode(order) {
  if (!order) return "-";
  return (
    order.codigo_publico ||
    order.codigo ||
    order.code ||
    (order.id ? `#${order.id}` : "Pedido")
  );
}

export function orderCustomer(order) {
  return (
    (order && (order.cliente_nome || order.cliente?.nome)) ||
    "Cliente não informado"
  );
}

export function orderDelivery(order) {
  return order?.delivery || order?.entrega || order?.entrega_operacional || {};
}

export function deliveryStatusForOrder(order) {
  const delivery = orderDelivery(order);
  return String(
    delivery.status ||
      delivery.situacao ||
      (order?.status === "saiu_para_entrega"
        ? "saiu_entrega"
        : order?.status === "entregue"
        ? "entregue"
        : "aguardando_atribuicao")
  ).toLowerCase();
}

export function isDeliveryAwaitingReturn(order) {
  return (
    (order?.tipo_entrega || order?.fulfillment_type) === "delivery" &&
    order?.status === "entregue" &&
    deliveryStatusForOrder(order) === "entregue"
  );
}

export function isOperationalPaymentConfirmed(order) {
  const context = order?.checkout_context || {};
  const operation = order?.operacao || order?.operational_link || {};
  const payment =
    order?.payment ||
    order?.pagamento ||
    (Array.isArray(order?.pagamentos) ? order.pagamentos[0] : null) ||
    {};

  return Boolean(
    order?.operational_payment_confirmed === true ||
      context.checkout_concluido === true ||
      context.operational_confirmed === true ||
      context.pagamento_confirmado === true ||
      payment.operationally_confirmed === true ||
      (operation.liquidado_at &&
        (operation.fechamento_id || operation.nfe_id || operation.pre_venda_id))
  );
}

export function normalizePaymentSummary(order) {
  const payment =
    order?.payment ||
    order?.pagamento ||
    (Array.isArray(order?.pagamentos) ? order.pagamentos[0] : null) ||
    {};
  const rawStatus = payment.status || payment.situacao || null;
  return {
    ...payment,
    metodo: payment.metodo || payment.method || payment.forma_pagamento || null,
    status: isOperationalPaymentConfirmed(order) ? "pago" : rawStatus,
    raw_status: payment.raw_status || rawStatus,
    operationally_confirmed: isOperationalPaymentConfirmed(order),
    valor: payment.valor ?? payment.amount ?? order?.total ?? 0,
  };
}

export function normalizeCard(card, laneKey = null, receivedAtMs = Date.now()) {
  const value = card || {};
  const previousReceivedAt = Number(value._received_at_ms);
  return {
    ...value,
    _received_at_ms: Number.isFinite(previousReceivedAt)
      ? previousReceivedAt
      : receivedAtMs,
    lane: value.lane || laneKey || null,
    item_count: Number(
      value.item_count ??
        value.itens_count ??
        value.quantidade_itens ??
        (Array.isArray(value.itens) ? value.itens.length : 0)
    ),
    payment: normalizePaymentSummary(value),
    production: value.production || value.producao || {},
    delivery:
      value.delivery || value.entrega || value.entrega_operacional || {},
    available_actions: Array.isArray(value.available_actions)
      ? value.available_actions
      : Array.isArray(value.capabilities?.actions)
      ? value.capabilities.actions
      : [],
  };
}

export function laneForOrder(order) {
  if (!order) return null;
  if (order.lane && BOARD_LANES.some((lane) => lane.key === order.lane)) {
    return order.lane;
  }

  const type = order.tipo_entrega || order.fulfillment_type;
  const status = order.status;
  return (
    BOARD_LANES.find(
      (lane) =>
        lane.statuses.includes(status) &&
        (!lane.deliveryType || lane.deliveryType === type)
    )?.key || null
  );
}

function normalizeLane(value, laneKey, receivedAtMs) {
  const source = Array.isArray(value) ? { items: value } : value || {};
  const items = source.items || source.data || source.pedidos || [];
  return {
    items: (Array.isArray(items) ? items : []).map((item) =>
      normalizeCard(item, laneKey, receivedAtMs)
    ),
    total: Number(source.total ?? source.count ?? items.length ?? 0),
    next_cursor: source.next_cursor || source.nextCursor || null,
  };
}

export function normalizeBoard(payload) {
  const value = unwrapResponse(payload);
  const lanesSource = value.lanes || value.colunas || {};
  const lanes = emptyBoard();
  const receivedAtMs = Date.now();

  BOARD_LANES.forEach((lane) => {
    lanes[lane.key] = normalizeLane(
      lanesSource[lane.key],
      lane.key,
      receivedAtMs
    );
  });

  return {
    lanes,
    server_time: value.server_time || value.serverTime || null,
    timezone: value.timezone || "America/Sao_Paulo",
    revision: value.revision || null,
    scope: value.scope || "active",
    stale_total: Number(value.stale_total || value.pendencias_antigas || 0),
  };
}

export function boardFromLegacyList(payload, scope = "active") {
  const value = unwrapResponse(payload);
  const rawItems = Array.isArray(value)
    ? value
    : value.items || value.pedidos?.data || value.pedidos || value.data || [];
  const items = Array.isArray(rawItems) ? rawItems : [];
  const lanes = emptyBoard();
  const receivedAtMs = Date.now();

  items.forEach((rawOrder) => {
    const order = normalizeCard(rawOrder, null, receivedAtMs);
    const laneKey = laneForOrder(order);
    if (!laneKey) return;
    lanes[laneKey].items.push({ ...order, lane: laneKey });
    lanes[laneKey].total += 1;
  });

  return {
    lanes,
    server_time: value.server_time || new Date().toISOString(),
    timezone: value.timezone || "America/Sao_Paulo",
    revision: null,
    scope,
    stale_total: Number(value.stale_total || 0),
  };
}

export function normalizeHistory(payload) {
  const value = unwrapResponse(payload);
  const receivedAtMs = Date.now();
  const items = Array.isArray(value)
    ? value
    : value.items || value.pedidos?.data || value.pedidos || [];
  return {
    items: (Array.isArray(items) ? items : []).map((item) =>
      normalizeCard(item, laneForOrder(item), receivedAtMs)
    ),
    total: Number(value.total ?? value.meta?.total ?? items.length ?? 0),
    next_cursor: value.next_cursor || value.nextCursor || null,
    server_time: value.server_time || null,
    timezone: value.timezone || "America/Sao_Paulo",
  };
}

export function mergeLane(currentLane, incomingLane, append = false) {
  if (!append) return incomingLane;
  const byId = new Map();
  [...(currentLane?.items || []), ...(incomingLane?.items || [])].forEach(
    (item) => byId.set(String(orderId(item)), item)
  );
  return {
    ...incomingLane,
    items: Array.from(byId.values()),
  };
}

export function formatMoney(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function stageAgeSeconds(order, serverTime, clientNow = Date.now()) {
  const baseAge = Number(order?.age_seconds);
  const receivedAtMs = Number(order?._received_at_ms);
  const elapsedSinceReceipt = Number.isFinite(receivedAtMs)
    ? Math.max(0, Math.floor((clientNow - receivedAtMs) / 1000))
    : 0;

  if (Number.isFinite(baseAge)) {
    return Math.max(0, baseAge + elapsedSinceReceipt);
  }

  const enteredAt =
    order?.stage_entered_at || order?.status_changed_at || order?.created_at;
  const enteredMs = enteredAt ? new Date(enteredAt).getTime() : NaN;
  const serverMs = serverTime ? new Date(serverTime).getTime() : NaN;

  if (
    Number.isFinite(enteredMs) &&
    Number.isFinite(serverMs) &&
    Number.isFinite(receivedAtMs)
  ) {
    return Math.max(
      0,
      Math.floor((serverMs - enteredMs) / 1000) + elapsedSinceReceipt
    );
  }

  return Number.isFinite(enteredMs)
    ? Math.max(0, Math.floor((clientNow - enteredMs) / 1000))
    : 0;
}

export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  if (seconds < 60) return "agora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}min`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function uuidLike() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
